<template>
    <div class="gs-checkout-rule-editor gs-advanced-settings">
        <section class="gs-checkout-default-card">
            <div class="gs-checkout-default-card__copy">
                <small>Country allowlist</small>
                <h4>Limit checkout to specific delivery countries.</h4>
                <p>Leave this off to accept every country. When it is on, buyers can only complete checkout from the selected countries.</p>
            </div>
            <div class="gs-checkout-default-card__body">
                <div class="gs-advanced-settings__switch">
                    <div>
                        <strong>Restrict checkout countries</strong>
                        <span>Uses the shipping catalog so the same ISO country list is available at checkout.</span>
                    </div>
                    <gd-toggle
                        data-test="country-allowlist-toggle"
                        aria-label="Restrict checkout countries"
                        :value="settings.allowed_countries.enabled"
                        @input="setAllowlistEnabled"
                    />
                </div>
                <label v-if="settings.allowed_countries.enabled" class="gs-advanced-settings__field">
                    <span>Allowed countries</span>
                    <multi-select
                        data-test="allowed-countries-select"
                        v-model="selectedCountries"
                        track-by="alpha2"
                        label="name"
                        :options="countryOptions"
                        :multiple="true"
                        :searchable="true"
                        :close-on-select="false"
                        :allow-empty="true"
                        placeholder="Select one or more countries"
                    />
                    <em v-if="catalogError">{{ catalogError }}</em>
                </label>
            </div>
        </section>

        <section v-if="showRegionalDeliveryFees" class="gs-checkout-default-card">
            <div class="gs-checkout-default-card__copy">
                <small>Regional delivery fees</small>
                <h4>Charge a different delivery fee by region.</h4>
                <p>The default fee applies when no region matches. Matching is done by state, postal prefix, or city on the existing checkout address fields.</p>
            </div>
            <div class="gs-checkout-default-card__body">
                <div class="gs-advanced-settings__switch">
                    <div>
                        <strong>Enable regional delivery fees</strong>
                        <span>Added once per order after coupons. Existing products stay unchanged while this is off.</span>
                    </div>
                    <gd-toggle
                        data-test="regional-fees-toggle"
                        aria-label="Enable regional delivery fees"
                        :value="settings.regional_delivery_fees.enabled"
                        @input="setRegionalFeesEnabled"
                    />
                </div>

                <div v-if="settings.regional_delivery_fees.enabled" class="gs-advanced-settings__fee-grid">
                    <label class="gs-advanced-settings__field">
                        <span>Default delivery fee</span>
                        <gd-currency-input
                            data-test="default-fee-input"
                            :value="defaultFeeDollars"
                            :currency-code="currencyCode"
                            @input="setDefaultFeeDollars"
                        />
                    </label>
                    <div class="gs-advanced-settings__region-actions">
                        <button
                            type="button"
                            class="gs-checkout-secondary-button"
                            data-test="add-west-malaysia"
                            @click="addMalaysiaRegion('west')"
                        >
                            Add West Malaysia
                        </button>
                        <button
                            type="button"
                            class="gs-checkout-secondary-button"
                            data-test="add-east-malaysia"
                            @click="addMalaysiaRegion('east')"
                        >
                            Add East Malaysia
                        </button>
                        <button
                            type="button"
                            class="gs-checkout-editor__primary"
                            data-test="add-region"
                            @click="addRegion"
                        >
                            Add region
                        </button>
                    </div>
                </div>

                <p v-if="settings.regional_delivery_fees.enabled" class="gs-advanced-settings__hint">
                    Malaysia West/East presets load Johor, Selangor, and other peninsula states versus Sabah, Sarawak, and Labuan.
                </p>
            </div>
        </section>

        <section
            v-if="showRegionalDeliveryFees && settings.regional_delivery_fees.enabled"
            class="gs-checkout-price-rules"
            data-test="region-list"
        >
            <div class="gs-checkout-price-rules__head">
                <div>
                    <h4>Delivery regions</h4>
                    <p>Name the region, choose how it matches a buyer address, and set the fee in the product currency.</p>
                </div>
            </div>

            <p v-if="!settings.regional_delivery_fees.regions.length" class="gs-checkout-empty-note">
                No regions yet. Add a custom region or use the Malaysia West/East shortcuts.
            </p>

            <article
                v-for="(region, index) in settings.regional_delivery_fees.regions"
                :key="region.id || index"
                class="gs-checkout-price-rule"
                :data-test="`region-card-${region.id || index}`"
            >
                <header class="gs-checkout-price-rule__head">
                    <div>
                        <strong>{{ region.name || 'Untitled region' }}</strong>
                        <span>{{ regionSummary(region) }}</span>
                    </div>
                    <div class="gs-checkout-price-rule__actions">
                        <button type="button" data-test="remove-region" @click="removeRegion(index)">
                            Remove
                        </button>
                    </div>
                </header>
                <div class="gs-checkout-price-rule__body">
                    <div class="gs-advanced-settings__region-grid">
                        <label class="gs-advanced-settings__field">
                            <span>Region name</span>
                            <text-input
                                :value="region.name"
                                placeholder="West Malaysia"
                                @input="setRegionField(region, 'name', $event)"
                            />
                        </label>
                        <label class="gs-advanced-settings__field">
                            <span>Country</span>
                            <gd-dropdown
                                :value="region.country"
                                size="full"
                                :options="countryDropdownOptions"
                                @input="setRegionCountry(region, $event)"
                                @change="setRegionCountry(region, $event)"
                            />
                        </label>
                        <label class="gs-advanced-settings__field">
                            <span>Match type</span>
                            <gd-dropdown
                                :value="region.match_type"
                                size="full"
                                :options="matchTypeOptions"
                                @input="setRegionField(region, 'match_type', $event)"
                                @change="setRegionField(region, 'match_type', $event)"
                            />
                        </label>
                        <label class="gs-advanced-settings__field">
                            <span>Fee</span>
                            <gd-currency-input
                                :value="centsToDollars(region.fee_cents)"
                                :currency-code="currencyCode"
                                @input="setRegionFeeDollars(region, $event)"
                            />
                        </label>
                    </div>
                    <label class="gs-advanced-settings__field">
                        <span>{{ matchValuesLabel(region) }}</span>
                        <multi-select
                            :value="selectedMatchValues(region)"
                            :options="matchValueOptions(region)"
                            :multiple="true"
                            :searchable="true"
                            :taggable="true"
                            :close-on-select="false"
                            :allow-empty="true"
                            placeholder="Select or type match values"
                            @input="setRegionMatchSelections(region, $event)"
                            @tag="addMatchValueTag(region, $event)"
                        />
                    </label>
                    <div v-if="region.country === 'MY' && region.match_type === 'state'" class="gs-advanced-settings__preset-row">
                        <button type="button" class="gs-checkout-secondary-button" @click="fillMalaysiaStates(region, 'west')">
                            Use West Malaysia states
                        </button>
                        <button type="button" class="gs-checkout-secondary-button" @click="fillMalaysiaStates(region, 'east')">
                            Use East Malaysia states
                        </button>
                    </div>
                </div>
            </article>
        </section>
    </div>
</template>

<script>
import axios from 'axios';
import {
    MATCH_TYPES,
    SHOW_REGIONAL_DELIVERY_FEES,
    centsToDollars,
    createEmptyRegion,
    dollarsToCents,
    forceRegionalDeliveryFeesOff,
    malaysiaPreset,
    normalizeAdvancedSettings,
    normalizeCountryCode,
} from './shippingSettings';

export default {
    name: 'AdvancedSettings',
    props: {
        model: {
            required: true,
        },
    },
    data() {
        return {
            localModel: this.model,
            countries: [],
            regionsByCountry: {},
            catalogError: '',
            matchTypeOptions: MATCH_TYPES,
        };
    },
    computed: {
        settings() {
            const options = this.localModel.checkout_page_options || {};
            return options.advanced_settings || normalizeAdvancedSettings();
        },
        showRegionalDeliveryFees() {
            return SHOW_REGIONAL_DELIVERY_FEES;
        },
        currencyCode() {
            const firstPricePoint = (this.localModel.pricing_points || [])[0] || {};
            return firstPricePoint.currency_code || this.localModel.currency_code || 'USD';
        },
        countryOptions() {
            return this.countries.map((country) => ({
                alpha2: country.alpha2,
                name: country.name,
            }));
        },
        countryDropdownOptions() {
            const options = this.countries.map((country) => ({
                value: country.alpha2,
                text: country.name,
            }));
            (this.settings.regional_delivery_fees.regions || []).forEach((region) => {
                if (region.country && !options.some((option) => option.value === region.country)) {
                    options.push({ value: region.country, text: region.country });
                }
            });
            return options;
        },
        selectedCountries: {
            get() {
                const selected = this.settings.allowed_countries.countries || [];
                return this.countryOptions.filter((country) => selected.includes(country.alpha2));
            },
            set(value) {
                this.settings.allowed_countries.countries = (value || []).map((country) => country.alpha2);
            },
        },
        defaultFeeDollars() {
            return centsToDollars(this.settings.regional_delivery_fees.default_fee_cents);
        },
    },
    watch: {
        localModel: {
            handler() {
                this.ensureSettings();
                this.$emit('update-model', this.localModel);
                this.$emit('updateModel', this.localModel);
            },
            deep: true,
        },
    },
    created() {
        this.ensureSettings();
    },
    mounted() {
        this.loadCatalog().then(() => {
            const countries = Array.from(new Set(
                (this.settings.regional_delivery_fees.regions || [])
                    .map((region) => region.country)
                    .filter(Boolean)
            ));
            return Promise.all(countries.map((country) => this.loadCatalog(country)));
        });
    },
    methods: {
        centsToDollars,
        ensureSettings() {
            if (!this.localModel.checkout_page_options) {
                this.$set(this.localModel, 'checkout_page_options', {});
            }
            const current = this.localModel.checkout_page_options.advanced_settings;
            if (!current || typeof current !== 'object' || !current.allowed_countries || !current.regional_delivery_fees) {
                const normalized = normalizeAdvancedSettings(current);
                if (
                    !SHOW_REGIONAL_DELIVERY_FEES
                    && current
                    && current.regional_delivery_fees
                    && typeof current.regional_delivery_fees === 'object'
                ) {
                    normalized.regional_delivery_fees.default_fee_cents = current.regional_delivery_fees.default_fee_cents;
                    normalized.regional_delivery_fees.regions = current.regional_delivery_fees.regions;
                }
                this.$set(
                    this.localModel.checkout_page_options,
                    'advanced_settings',
                    normalized
                );
            }
            forceRegionalDeliveryFeesOff(this.localModel.checkout_page_options.advanced_settings);
            return this.localModel.checkout_page_options.advanced_settings;
        },
        setAllowlistEnabled(value) {
            this.settings.allowed_countries.enabled = typeof value === 'boolean' ? value : !this.settings.allowed_countries.enabled;
        },
        setRegionalFeesEnabled(value) {
            this.toggleFees(value);
        },
        toggleFees(value) {
            if (!SHOW_REGIONAL_DELIVERY_FEES) {
                return;
            }
            this.settings.regional_delivery_fees.enabled = typeof value === 'boolean' ? value : !this.settings.regional_delivery_fees.enabled;
        },
        setDefaultFeeDollars(value) {
            this.settings.regional_delivery_fees.default_fee_cents = dollarsToCents(value);
        },
        setRegionField(region, key, value) {
            this.$set(region, key, value);
        },
        setRegionFeeDollars(region, value) {
            this.$set(region, 'fee_cents', dollarsToCents(value));
        },
        setRegionCountry(region, value) {
            const country = normalizeCountryCode(value && value.value ? value.value : value);
            this.$set(region, 'country', country);
            if (country) {
                this.loadCatalog(country);
            }
        },
        addRegion() {
            this.settings.regional_delivery_fees.regions.push(createEmptyRegion({
                name: '',
                country: (this.settings.allowed_countries.countries || [])[0] || '',
            }));
        },
        removeRegion(index) {
            this.settings.regional_delivery_fees.regions.splice(index, 1);
        },
        async addMalaysiaRegion(kind) {
            return this.applyPreset(kind);
        },
        async applyPreset(kind) {
            if (!SHOW_REGIONAL_DELIVERY_FEES) {
                return;
            }
            this.settings.regional_delivery_fees.enabled = true;
            await this.loadCatalog('MY');
            const preset = malaysiaPreset(kind, this.regionsByCountry.MY || []);
            const existingIndex = this.settings.regional_delivery_fees.regions.findIndex((region) => region.id === preset.id);
            if (existingIndex >= 0) {
                this.$set(this.settings.regional_delivery_fees.regions, existingIndex, {
                    ...this.settings.regional_delivery_fees.regions[existingIndex],
                    ...preset,
                    fee_cents: this.settings.regional_delivery_fees.regions[existingIndex].fee_cents,
                });
                return;
            }
            this.settings.regional_delivery_fees.regions.push(preset);
        },
        async fillMalaysiaStates(region, kind) {
            if (!SHOW_REGIONAL_DELIVERY_FEES) {
                return;
            }
            await this.loadCatalog('MY');
            const preset = malaysiaPreset(kind, this.regionsByCountry.MY || []);
            this.$set(region, 'country', 'MY');
            this.$set(region, 'match_type', 'state');
            this.$set(region, 'match_values', preset.match_values);
            if (!region.name) {
                this.$set(region, 'name', preset.name);
            }
            if (!region.id || String(region.id).indexOf('region-') === 0) {
                this.$set(region, 'id', preset.id);
            }
        },
        selectedMatchValues(region) {
            return (region.match_values || []).map((value) => ({
                value,
                text: this.matchValueLabel(region, value),
            }));
        },
        matchValueOptions(region) {
            const catalog = this.regionsByCountry[region.country] || [];
            const options = catalog.map((item) => ({
                value: item.name,
                text: item.name,
            }));
            (region.match_values || []).forEach((value) => {
                if (!options.some((option) => option.value === value)) {
                    options.push({ value, text: value });
                }
            });
            return options;
        },
        matchValueLabel(region, value) {
            const match = (this.regionsByCountry[region.country] || []).find((item) => {
                return item.name === value || item.code === value;
            });
            return match ? match.name : value;
        },
        setRegionMatchSelections(region, selections) {
            const values = (selections || []).map((item) => {
                if (item && typeof item === 'object') {
                    return item.value || item.text || '';
                }
                return item;
            });
            this.$set(region, 'match_values', values.filter(Boolean));
        },
        addMatchValueTag(region, label) {
            const value = String(label || '').trim();
            if (!value) {
                return;
            }
            const next = (region.match_values || []).slice();
            if (!next.includes(value)) {
                next.push(value);
                this.$set(region, 'match_values', next);
            }
        },
        matchValuesLabel(region) {
            if (region.match_type === 'postal_prefix') {
                return 'Postal prefixes';
            }
            if (region.match_type === 'city') {
                return 'Cities';
            }
            return 'States / regions';
        },
        regionSummary(region) {
            const country = this.countryOptions.find((item) => item.alpha2 === region.country);
            const countryLabel = country ? country.name : (region.country || 'No country');
            const count = (region.match_values || []).length;
            const fee = centsToDollars(region.fee_cents).toFixed(2);
            return `${countryLabel} · ${count} match ${count === 1 ? 'value' : 'values'} · ${fee} ${this.currencyCode}`;
        },
        async loadCatalog(country) {
            const queryCountry = normalizeCountryCode(country);
            if (queryCountry && this.regionsByCountry[queryCountry]) {
                return;
            }
            try {
                const { data } = await axios.get('shipping-catalog', {
                    params: queryCountry ? { country: queryCountry } : {},
                });
                this.countries = Array.isArray(data && data.countries) ? data.countries : this.countries;
                if (queryCountry) {
                    this.$set(this.regionsByCountry, queryCountry, Array.isArray(data && data.regions) ? data.regions : []);
                }
                this.catalogError = '';
            } catch (error) {
                this.catalogError = 'Unable to load the shipping catalog. You can still keep existing values and try again later.';
            }
        },
    },
};
</script>

<style>
.gs-advanced-settings__switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 14px 16px;
    background: var(--gs-surface-subtle);
    border: 1px solid var(--gs-border);
    border-radius: var(--gs-radius-md);
}

.gs-advanced-settings__switch strong {
    display: block;
    color: var(--gs-foreground);
    font-size: 15px;
    font-weight: 650;
}

.gs-advanced-settings__switch span {
    display: block;
    margin-top: 4px;
    color: var(--gs-muted-foreground);
    font-size: 13px;
    line-height: 1.4;
}

.gs-advanced-settings__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.gs-advanced-settings__field span {
    color: var(--gs-foreground);
    font-size: 14px;
    font-weight: 600;
}

.gs-advanced-settings__field em {
    color: var(--gs-muted-foreground);
    font-size: 13px;
    font-style: normal;
}

.gs-advanced-settings__fee-grid,
.gs-advanced-settings__region-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 14px;
    align-items: end;
}

.gs-advanced-settings__region-actions,
.gs-advanced-settings__preset-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
}

.gs-advanced-settings__hint {
    margin: 0;
    color: var(--gs-muted-foreground);
    font-size: 13px;
    line-height: 1.45;
}

@media (max-width: 720px) {
    .gs-advanced-settings__fee-grid,
    .gs-advanced-settings__region-grid {
        grid-template-columns: 1fr;
    }

    .gs-advanced-settings__switch {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
